import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  ConfigurationSetting,
  ConfigurationSettingCodes,
  insightsHeader,
  Octopus,
  SystemConfiguration,
} from '@maximizer/core/shared/domain';
import {
  isNullOrEmpty,
  isNullOrUndefinedOrEmpty,
  splitStringWithIndex,
} from '@maximizer/shared/util';
import { forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { ContextService } from './context.service';

export interface ReadConfigurationSetting<TObject> {
  readObject: TObject;
  settings: ConfigurationSetting[];
}

@Injectable()
export class ConfigurationService {
  private static readonly Bulkprefix = 'ConfigurationSetting.';
  private static readonly CodeMaxLength = 32;
  private static readonly CharsBeforeSplit = 3000;

  constructor(
    private readonly http: HttpClient,
    private readonly context: ContextService,
  ) {}

  getSystemConfiguration(): Observable<SystemConfiguration> {
    const request: Octopus.SystemConfigurationReadRequest = {
      Configuration: {
        SystemOption: {
          FiscalYearStartMonth: 1,
          MultiCurrencyEnabled: 1,
          CorporateCurrency: 1,
        },
      },
    };

    return this.http
      .post<Octopus.SystemConfigurationResponse>(
        `${this.context.api}${Octopus.Action.READ_CONFIGURATION}`,
        request,
      )
      .pipe(
        map((result) => {
          const mapper = new Octopus.SystemConfigurationMapper();
          return mapper.from(result.Configuration.Data);
        }),
      );
  }

  private getConfigurationSettingQuery(
    filter: ConfigurationSettingCodes | ConfigurationSettingCodes[],
    operators?: Partial<
      Record<keyof ConfigurationSettingCodes, Octopus.ComparisonOperator>
    >,
  ): Octopus.LogicalQuery<Octopus.ConfigurationSetting> {
    const filters = Array.isArray(filter) ? filter : [filter];
    const andQuery = filters.map((codes) => {
      const condition: Octopus.LogicalQuery<Octopus.ConfigurationSetting> = {
        $AND: [],
      };
      if (codes.code1) {
        condition.$AND?.push({
          Code1: { [operators?.code1 ?? '$EQ']: codes.code1 },
        });
      }
      if (codes.code2) {
        condition.$AND?.push({
          Code2: { [operators?.code2 ?? '$EQ']: codes.code2 },
        });
      }
      if (codes.code3) {
        condition.$AND?.push({
          Code3: { [operators?.code3 ?? '$EQ']: codes.code3 },
        });
      }
      if (codes.code4) {
        condition.$AND?.push({
          Code4: { [operators?.code4 ?? '$EQ']: codes.code4 },
        });
      }
      return condition;
    });

    return filters.length > 1
      ? {
          $OR: [...andQuery],
        }
      : andQuery[0];
  }

  private getConfigurationSettingRequest(
    query: Octopus.LogicalQuery<Octopus.ConfigurationSetting>,
  ): Octopus.ConfigurationSettingReadRequest {
    return {
      ConfigurationSetting: {
        Criteria: {
          SearchQuery: query,
        },
        Scope: {
          Fields: {
            Key: 1,
            Code1: 1,
            Code2: 1,
            Code3: 1,
            Code4: 1,
            TextValue: 1,
            IntValue: 1,
            CreationDate: 1,
            LastModifyDate: 1,
          },
        },
      },
    };
  }

  getSetting(
    filter: ConfigurationSettingCodes,
    operators?: Partial<
      Record<keyof ConfigurationSettingCodes, Octopus.ComparisonOperator>
    >,
  ): Observable<ConfigurationSetting | null> {
    return this.getSettings(filter, operators).pipe(
      map((settings) => {
        if (settings?.length) {
          return settings[0];
        }
        return null;
      }),
    );
  }

  getSettings(
    filter: ConfigurationSettingCodes | ConfigurationSettingCodes[],
    operators?: Partial<
      Record<keyof ConfigurationSettingCodes, Octopus.ComparisonOperator>
    >,
  ): Observable<ConfigurationSetting[] | null> {
    const query = this.getConfigurationSettingQuery(filter, operators);
    const request = this.getConfigurationSettingRequest(query);

    return this.http
      .post<Octopus.ConfigurationSettingResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.ConfigurationSetting.Data.length > 0) {
            const mapper = new Octopus.ConfigurationSettingMapper();
            return result.ConfigurationSetting.Data.map((item) =>
              mapper.from(item),
            );
          }
          return null;
        }),
      );
  }

  getBulkSettingsChunks<TReadObject>(
    code4: string,
    settingCodes: ConfigurationSettingCodes,
  ): Observable<ReadConfigurationSetting<TReadObject> | undefined> {
    return this.getSettings(
      [
        {
          ...settingCodes,
          code4: `%${code4}.%`,
        },
      ],
      { code4: '$LIKE' },
    ).pipe(
      switchMap((settings: ConfigurationSetting[] | null) => {
        settings = settings || [];
        settings =
          (settings ?? [])?.sort((a, b) => {
            const firstCode4 = Number((a.code4 ?? '.0').split('.')[1]);
            const secondCode4 = Number((b.code4 ?? '.0').split('.')[1]);
            if (firstCode4 < secondCode4) return -1;
            if (firstCode4 > secondCode4) return 1;
            return 0;
          }) || [];

        if (settings.length) {
          const combinedTextValue = settings.reduce(
            (acc, result) => acc + (result?.textValue ?? ''),
            '',
          );

          if (!isNullOrEmpty(combinedTextValue)) {
            const readObject: TReadObject = combinedTextValue
              ? JSON.parse(combinedTextValue)
              : undefined;
            return of({
              readObject,
              settings,
            });
          }
        }
        return of(undefined);
      }),
    );
  }

  updateSetting(
    id: string | null,
    codes: ConfigurationSettingCodes,
    textValue?: string,
    intValue?: number | null,
  ): Observable<ConfigurationSetting | null> {
    const request: Octopus.ConfigurationSettingWriteRequest = {
      ConfigurationSetting: {
        Data: {
          Key: id,
          TextValue: textValue,
          IntValue: intValue,
        },
      },
    };

    if (id === null) {
      request.ConfigurationSetting.Data.Code1 = codes.code1 ?? '';
      request.ConfigurationSetting.Data.Code2 = codes.code2 ?? '';
      request.ConfigurationSetting.Data.Code3 = codes.code3 ?? '';
      request.ConfigurationSetting.Data.Code4 = codes.code4 ?? '';
    }

    return this.http
      .post<Octopus.ConfigurationSettingWriteResponse>(
        `${this.context.api}${
          id ? Octopus.Action.UPDATE : Octopus.Action.CREATE
        }`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            const mapper = new Octopus.ConfigurationSettingMapper();
            return mapper.from(result.ConfigurationSetting.Data);
          }
          return null;
        }),
      );
  }

  bulkUpdateSettingsChunks<TSaveObject>(
    code4: string,
    configurationSetting: ConfigurationSetting[],
    settingCodes: ConfigurationSettingCodes,
    objectToSave: TSaveObject,
  ): Observable<ConfigurationSetting[]> {
    this.validateConfigurationSetting(settingCodes);

    const splittedStrings = splitStringWithIndex(
      JSON.stringify(objectToSave),
      ConfigurationService.CharsBeforeSplit,
    );

    // Remove unused filters because of the new length
    const toRemove = configurationSetting.length - splittedStrings.length;

    if (toRemove > 0) {
      const unusedFilters = configurationSetting.splice(-toRemove, toRemove);
      this.bulkDelete(unusedFilters.map((filter) => filter?.id)).subscribe();
    }

    const result = splittedStrings.map((splittedObj) => {
      const { code1, code2, code3 } = settingCodes;
      return {
        Key: configurationSetting?.[splittedObj.index]?.id ?? null,
        TextValue: splittedObj.text,
        Code1: code1,
        Code2: code2,
        Code3: code3,
        Code4: `${code4}.${splittedObj.index}`,
      } as Octopus.ConfigurationSetting;
    });
    const createRequests = result.filter((x) =>
      isNullOrUndefinedOrEmpty(x.Key),
    );
    const updateRequests = result.filter(
      (x) => !isNullOrUndefinedOrEmpty(x.Key),
    );

    const update$ = updateRequests.length
      ? this.bulkUpdateSetting(Octopus.Action.UPDATE, updateRequests)
      : of([]);

    const create$ = createRequests.length
      ? this.bulkUpdateSetting(Octopus.Action.CREATE, createRequests)
      : of([]);

    return forkJoin([update$, create$]).pipe(
      map(([updated, created]) => [...updated, ...created]),
    );
  }

  bulkUpdateSetting(
    action: typeof Octopus.Action.CREATE | typeof Octopus.Action.UPDATE,
    configurationSettings: (Octopus.ConfigurationSetting | undefined)[],
  ): Observable<ConfigurationSetting[]> {
    const request: Record<
      string,
      { Data: Octopus.ConfigurationSetting | undefined }
    > = {};

    if (configurationSettings) {
      configurationSettings.forEach(
        (setting, index) =>
          (request[`${ConfigurationService.Bulkprefix}${index}`] = {
            Data: setting,
          }),
      );
    }

    return this.http
      .post<Octopus.ConfigurationSettingBulkWriteResponse>(
        `${this.context.api}${action}`,
        request,
        {
          headers: { [insightsHeader('entity')]: 'ConfigurationSetting' },
        },
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            const mapper = new Octopus.ConfigurationSettingMapper();

            return (
              Object.keys(result) as Array<
                keyof Octopus.ConfigurationSettingBulkWriteResponse
              >
            )
              .filter((k) => k?.startsWith(ConfigurationService.Bulkprefix))
              .map((key) => {
                const writeResponse =
                  result[key as `ConfigurationSetting.${number}`];
                return mapper.from(writeResponse.Data);
              });
          }
          return [];
        }),
      );
  }

  private validateConfigurationSetting(
    settingCodes: ConfigurationSettingCodes,
  ): void {
    if (
      (settingCodes.code1?.length ?? 0) > ConfigurationService.CodeMaxLength ||
      (settingCodes.code2?.length ?? 0) > ConfigurationService.CodeMaxLength ||
      (settingCodes.code3?.length ?? 0) > ConfigurationService.CodeMaxLength ||
      (settingCodes.code4?.length ?? 0) > ConfigurationService.CodeMaxLength
    ) {
      console.error(
        `The "code" property exceeds the maximum allowed length of ${ConfigurationService.CodeMaxLength} characters.`,
      );
    }
  }

  bulkDelete(configurationKeys: string[]): Observable<boolean> {
    const request: Record<string, { Data: { Key: string } }> = {};

    if (configurationKeys) {
      configurationKeys.forEach(
        (key, index) =>
          (request[`${ConfigurationService.Bulkprefix}${index}`] = {
            Data: { Key: key },
          }),
      );
    }

    return this.http
      .post<Octopus.Response>(
        `${this.context.api}${Octopus.Action.DELETE}`,
        request,
      )
      .pipe(
        map((result) => {
          return result.Code === Octopus.ResponseStatusCode.Successful;
        }),
      );
  }
}
